import React, { FC, ReactElement, ReactNode, useState, } from "react";

import { classNames,} from "components/Utils";
import { Modal, Select, NumberInput, ModalType, ButtonColor, Button, } from "components/FormControls";
import { DamageType, getFilledArray, Icons, } from ".";

export enum TrackerModalType {
    Damage = "damage",
    Heal = "heal",
};

type Props = {
    type: TrackerModalType;
    damage: DamageType[];
    onApply: (damage: DamageType[]) => void;
    onCancel: () => void;
    children?: ReactNode;
};

export const TrackerModal:FC<Props> = (props: Props): ReactElement => {
    const { type, damage, onApply, onCancel, } = props;

    const DEFAULT_AMOUNT = 1;
    const DEFAULT_DAMAGE_TYPE = DamageType.Superficial;

    // state
    const [damageType, setDamageType] = useState(DEFAULT_DAMAGE_TYPE);
    const [amount, setAmount] = useState(DEFAULT_AMOUNT);

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.currentTarget.value;

        switch (value.toLowerCase()) {
            case "aggravated":
                setDamageType(DamageType.Aggravated);
                break;

            case "superficial":
            default:
                setDamageType(DamageType.Superficial);
                break;
        }

        // console.log(value, damageType);
    };

    const handleNumberInputChange = (value: number) => {
        setAmount(value);
    };

    const handleConfirm = () => {
        const operation = type === TrackerModalType.Damage ? addDamage : removeDamage;
        const newDamage = operation(amount, damageType, damage);
        onApply(newDamage);
    };

    const handleExtraAction = () => {
        const operation = type === TrackerModalType.Damage ? instakill : instaheal;
        const newDamage = operation(damage);

        console.log(onApply);

        onApply(newDamage);
    };

    const extraActionButtons = [renderExtraActionButton(type, handleExtraAction)];

    const css = classNames([
        "__trackermodal__",
        type === TrackerModalType.Damage ? "__damagemodal__" : "__healmodal__",
    ]);

    return (
        <Modal
            title={getTitle(type)}
            onCancel={onCancel}
            onConfirm={handleConfirm}
            type={ModalType.Confirm}
            confirmLabel={"Apply"}
            extraActionButtons={extraActionButtons}
            className={css}
        >
            <div className="row mb-3">
                <label className="form-label">Damage Type</label>
                <Select
                    value={damageType}
                    onChange={handleSelectChange}
                    hideDefaultOption={true}
                >
                    <option value="aggravated">Aggravated</option>
                    <option value="superficial">Superficial</option>
                </Select>
            </div>

            <div className="row mb-3">
                <label className="form-label">Amount</label>
                <NumberInput
                    value={amount}
                    min={1}
                    max={10}
                    onChange={handleNumberInputChange}
                    showChangeButtons={true}
                />
            </div>
        </Modal>
    );
};

const getTitle = (type: TrackerModalType): string => {
    return type === TrackerModalType.Damage ? "Damage" : "Heal";
};

const removeDamage = (amount: number, type: DamageType, damage: DamageType[]): DamageType[] => {
    const size = damage.length;

    let aggravated = damage.filter(el => el === DamageType.Aggravated);
    let superficial = damage.filter(el => el === DamageType.Superficial);

    if (type === DamageType.Aggravated) {
        aggravated = aggravated.slice(0, amount * -1);
    } else if (type === DamageType.Superficial) {
        superficial = superficial.slice(0, amount * -1);
    }

    // fill the remainder with None
    const delta = size - (aggravated.length + superficial.length);
    const filler = getFilledArray(delta, DamageType.None);

    return [
        ...aggravated,
        ...superficial,
        ...filler,
    ];
};

const addDamage = (amount: number, type: DamageType, damage: DamageType[]): DamageType[] => {
    const max = damage.length;

    let aggravated = damage.filter(el => el === DamageType.Aggravated);
    const a = aggravated.length;
    let superficial = damage.filter(el => el === DamageType.Superficial);
    const s = superficial.length;
    let none = damage.filter(el => el === DamageType.None);
    const n = none.length;

    let delta = 0;

    // handle aggravated damage
    if (type === DamageType.Aggravated) {
        // handle the maximum case
        if (a + amount >= max) {
            return getFilledArray(max, DamageType.Aggravated);
        }

        // convert some none to aggrevated, leave superficial alone
        if (n && amount <= n) {
            delta = n - amount;

            return [
                ...getFilledArray(a + amount, DamageType.Aggravated),
                ...superficial,
                ...getFilledArray(delta, DamageType.None),
            ];
        }

        // change all superficial damage into aggravated
        if (amount >= s) {
            delta = Math.max((max - amount) + a, 0);

            return [
                ...getFilledArray(Math.min(max, a + amount), DamageType.Aggravated),
                ...getFilledArray(delta, DamageType.None),
            ];
        }

        // convert some superficial into aggrevated
        delta = s - amount;

        return [
            ...getFilledArray(a + amount, DamageType.Aggravated),
            ...getFilledArray(delta, DamageType.Superficial),
            ...none,
        ];
    }

    // handle superficial
    if (type === DamageType.Superficial) {
        // handle the minimum case: change some or all none to superficial
        if (amount <= n) {
            delta = n - amount;

            return [
                ...aggravated,
                ...getFilledArray(s + amount, DamageType.Superficial),
                ...getFilledArray(delta, DamageType.None),
            ];
        }

        // amount is greater than the available none space so we'll need to convert
        // some of those into aggravated
        delta = amount - n;

        return [
            ...getFilledArray(Math.min(max, a + delta), DamageType.Aggravated),
            ...getFilledArray(Math.max(0, max - (a + delta)), DamageType.Superficial),
        ];
    }

    // shouldn't ever get here, to be honest
    return damage;
};

// returns an array filled with aggravated damage
const instakill = (damage: DamageType[]) => {
    const size = damage.length;
    return getFilledArray(size, DamageType.Aggravated);
};

// returns an array filled with no damage
const instaheal = (damage: DamageType[]) => {
    const size = damage.length;
    return getFilledArray(size, DamageType.None);
};

const renderExtraActionButton = (type: TrackerModalType, onClick: () => void): ReactElement => {
    const color = type === TrackerModalType.Heal ? ButtonColor.Success: ButtonColor.Danger;
    const icon = type === TrackerModalType.Heal ? Icons.Instaheal : Icons.Instakill;
    const label = type === TrackerModalType.Heal ? "Instant Heal" : "Instant Incapacitation";
    const title = type === TrackerModalType.Heal
        ? "Completely empty the tracker"
        : "Completely fill the tracker with Aggravated damage";
    const css = "__trackermodalextrabutton__";

    return (
        <Button
            label={label}
            title={title}
            className={css}
            color={color}
            onClick={onClick}
            key={type.toLowerCase()}
        >
            {icon}
        </Button>
    );
};
